import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeaders, handleApiError } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../../components/ui/dialog';
import { Card } from '../../components/ui/card';
import { Plus, TrendingUp, TrendingDown, Wallet, CreditCard, Banknote, Smartphone, Building2, CircleDollarSign, Search, Calendar, Filter, ChevronDown, Check, X, FileText, ChevronLeft, ChevronRight, Pencil, AlertTriangle, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { usePrintShortcut, generatePrintContent, openPrintWindow, formatPrintCurrency, formatPrintDate } from '../../utils/printUtils';

// Month Selector Component
const MonthSelector = ({ value, onChange }) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const [year, month] = value.split('-').map(Number);
  const monthName = months[month - 1];
  
  const goToPreviousMonth = () => {
    if (month === 1) {
      onChange(`${year - 1}-12`);
    } else {
      onChange(`${year}-${String(month - 1).padStart(2, '0')}`);
    }
  };
  
  const goToNextMonth = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    
    // Don't go beyond current month
    if (year > currentYear || (year === currentYear && month >= currentMonth)) {
      return;
    }
    
    if (month === 12) {
      onChange(`${year + 1}-01`);
    } else {
      onChange(`${year}-${String(month + 1).padStart(2, '0')}`);
    }
  };
  
  const isCurrentMonth = () => {
    const now = new Date();
    return year === now.getFullYear() && month === now.getMonth() + 1;
  };
  
  return (
    <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1.5">
      <button
        onClick={goToPreviousMonth}
        className="p-1 hover:bg-slate-100 rounded transition-colors"
      >
        <ChevronLeft className="h-4 w-4 text-slate-600" />
      </button>
      <div className="flex items-center gap-1.5 min-w-[140px] justify-center">
        <Calendar className="h-4 w-4 text-blue-500" />
        <span className="text-sm font-medium text-slate-700">{monthName} {year}</span>
      </div>
      <button
        onClick={goToNextMonth}
        disabled={isCurrentMonth()}
        className={`p-1 rounded transition-colors ${
          isCurrentMonth() ? 'text-slate-300 cursor-not-allowed' : 'hover:bg-slate-100 text-slate-600'
        }`}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

// Payment mode options with icons - for Withdrawals (only Cash, UPI, Cheque)
const paymentModes = [
  { value: 'cash', label: 'Cash', icon: Banknote, color: 'text-green-600 bg-green-50 border-green-200' },
  { value: 'upi', label: 'UPI', icon: Smartphone, color: 'text-purple-600 bg-purple-50 border-purple-200' },
  { value: 'cheque', label: 'Cheque', icon: CircleDollarSign, color: 'text-blue-600 bg-blue-50 border-blue-200' },
];

// Deposit-specific payment modes (limited options)
const depositModes = [
  { value: 'cash_deposit', label: 'Cash Deposit', icon: Banknote, color: 'text-green-600 bg-green-50 border-green-200' },
  { value: 'daily_paytm', label: 'Daily Paytm Settlement', icon: Wallet, color: 'text-sky-600 bg-sky-50 border-sky-200' },
];

// Party Dropdown Component (Unified - for distributors and expenses)
const PartyDropdown = ({ parties, value, onChange, placeholder = "Select Party" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  
  const filteredParties = parties.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  // Group parties by type
  const distributors = filteredParties.filter(p => p.type === 'distributor');
  const expenses = filteredParties.filter(p => p.type === 'expense');
  const others = filteredParties.filter(p => p.type !== 'distributor' && p.type !== 'expense');

  const selectedParty = parties.find(p => p.id === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderPartyItem = (party) => (
    <button
      key={party.id}
      type="button"
      onClick={() => {
        onChange(party);
        setIsOpen(false);
        setSearch('');
      }}
      className={`w-full px-3 py-2 flex items-center justify-between hover:bg-slate-50 transition-colors text-left ${
        value === party.id ? 'bg-blue-50' : ''
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${
          party.type === 'distributor' ? 'bg-blue-500' : 
          party.type === 'expense' ? 'bg-amber-500' : 'bg-slate-400'
        }`}></span>
        <span className="font-medium text-slate-900 text-sm">{party.name}</span>
      </div>
      {value === party.id && <Check className="h-4 w-4 text-blue-600" />}
    </button>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-11 px-3 flex items-center justify-between border border-slate-200 rounded-lg bg-white hover:border-slate-300 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {selectedParty && (
            <span className={`w-2 h-2 rounded-full ${
              selectedParty.type === 'distributor' ? 'bg-blue-500' : 
              selectedParty.type === 'expense' ? 'bg-amber-500' : 'bg-slate-400'
            }`}></span>
          )}
          <span className={selectedParty ? 'text-slate-900' : 'text-slate-400'}>
            {selectedParty ? selectedParty.name : placeholder}
          </span>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search party..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-64">
            {filteredParties.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-slate-500">
                No parties found
              </div>
            ) : (
              <>
                {/* Distributors */}
                {distributors.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 sticky top-0">
                      Distributors ({distributors.length})
                    </div>
                    {distributors.map(renderPartyItem)}
                  </>
                )}
                
                {/* Expenses */}
                {expenses.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-xs font-semibold text-amber-600 bg-amber-50 sticky top-0">
                      Expenses ({expenses.length})
                    </div>
                    {expenses.map(renderPartyItem)}
                  </>
                )}
                
                {/* Others */}
                {others.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-50 sticky top-0">
                      Others ({others.length})
                    </div>
                    {others.map(renderPartyItem)}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ACStatement = () => {
  const [statements, setStatements] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showConfirmEdit, setShowConfirmEdit] = useState(false);
  const [pendingEditData, setPendingEditData] = useState(null);
  
  // Month filter - default to current month
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  
  // Monthly summary
  const [monthlySummary, setMonthlySummary] = useState({
    opening_balance: 0,
    total_deposits: 0,
    total_withdrawals: 0,
    closing_balance: 0
  });
  
  // Filters
  const [filterMode, setFilterMode] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [newEntry, setNewEntry] = useState({
    type: 'credit',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    party_id: '',
    party_name: '',
    party_type: '',
    cheque_ref: '',
    payment_mode: 'cash_deposit'
  });

  const resetForm = () => {
    setNewEntry({
      type: 'credit',
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      party_id: '',
      party_name: '',
      party_type: '',
      cheque_ref: '',
      payment_mode: 'cash_deposit'
    });
    setEditingEntry(null);
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setNewEntry({
      type: entry.type === 'credit' ? 'credit' : 'debit',
      amount: entry.amount.toString(),
      description: entry.description || '',
      date: entry.date,
      party_id: entry.party_id || '',
      party_name: entry.party_name || '',
      party_type: entry.party_type || '',
      cheque_ref: entry.cheque_ref || '',
      payment_mode: entry.payment_mode || 'cash_deposit'
    });
    setShowAdd(true);
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // First seed preloaded parties (will skip if already exists)
      await axios.post(`${API}/parties/seed`, {}, { headers: getAuthHeaders() }).catch(() => {});
      
      const [monthlyRes, partiesRes] = await Promise.all([
        axios.get(`${API}/ac-statement/monthly?firm=pharmacy&month=${selectedMonth}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/parties`, { headers: getAuthHeaders() })
      ]);
      
      setStatements(monthlyRes.data.transactions || []);
      setMonthlySummary({
        opening_balance: monthlyRes.data.opening_balance || 0,
        total_deposits: monthlyRes.data.total_deposits || 0,
        total_withdrawals: monthlyRes.data.total_withdrawals || 0,
        closing_balance: monthlyRes.data.closing_balance || 0
      });
      setParties(partiesRes.data);
    } catch (error) {
      const message = handleApiError(error, 'Failed to fetch data');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePartySelect = (party) => {
    setNewEntry({ 
      ...newEntry, 
      party_id: party.id, 
      party_name: party.name,
      party_type: party.type  // Track party type for smart logic
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Party is required only for withdrawals
    if (newEntry.type === 'debit' && !newEntry.party_id) {
      toast.error('Please select a party');
      return;
    }

    // If editing, show confirmation dialog
    if (editingEntry) {
      setPendingEditData({
        firm: 'pharmacy',
        ...newEntry,
        amount: parseFloat(newEntry.amount)
      });
      setShowConfirmEdit(true);
      return;
    }
    
    // Add new entry
    try {
      await axios.post(`${API}/ac-statement`, {
        firm: 'pharmacy',
        ...newEntry,
        amount: parseFloat(newEntry.amount)
      }, { headers: getAuthHeaders() });
      
      toast.success('Entry added successfully');
      setShowAdd(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to add entry');
    }
  };

  const confirmEdit = async () => {
    if (!pendingEditData || !editingEntry) return;
    
    try {
      await axios.put(`${API}/ac-statement/${editingEntry.id}`, pendingEditData, { headers: getAuthHeaders() });
      
      toast.success('Entry updated successfully');
      setShowConfirmEdit(false);
      setShowAdd(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to update entry');
    }
  };

  // Calculate totals
  const calculateTotals = (stmts = statements) => {
    let deposits = 0, withdrawals = 0;
    stmts.forEach(stmt => {
      if (stmt.type === 'credit') deposits += stmt.amount;
      else withdrawals += stmt.amount;
    });
    return { deposits, withdrawals, balance: deposits - withdrawals };
  };

  // Filter statements
  const filteredStatements = statements.filter(stmt => {
    // Payment mode filter
    if (filterMode !== 'all' && stmt.payment_mode !== filterMode) return false;
    
    // Search filter (party name or description)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesParty = stmt.party_name?.toLowerCase().includes(query);
      const matchesDesc = stmt.description?.toLowerCase().includes(query);
      if (!matchesParty && !matchesDesc) return false;
    }
    
    return true;
  });

  const totals = calculateTotals(filteredStatements);

  // Print function for Account Statement
  const handlePrint = useCallback(() => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const [year, month] = selectedMonth.split('-').map(Number);
    const dateRange = `${months[month - 1]} ${year}`;
    
    const headers = [
      { label: 'Date', align: 'left' },
      { label: 'Type', align: 'center' },
      { label: 'Party', align: 'left' },
      { label: 'Mode', align: 'center' },
      { label: 'Deposit', align: 'right' },
      { label: 'Withdrawal', align: 'right' },
      { label: 'Balance', align: 'right' }
    ];

    let runningBalance = monthlySummary.opening_balance || 0;
    const rows = filteredStatements.map(stmt => {
      if (stmt.type === 'credit') {
        runningBalance += stmt.amount;
      } else {
        runningBalance -= stmt.amount;
      }
      return [
        formatPrintDate(stmt.date),
        `<span class="badge ${stmt.type === 'credit' ? 'badge-green' : 'badge-red'}">${stmt.type === 'credit' ? 'Deposit' : 'Withdrawal'}</span>`,
        stmt.party_name || '-',
        stmt.payment_mode?.replace('_', ' ').toUpperCase() || '-',
        stmt.type === 'credit' ? `<span class="amount-positive">+${formatPrintCurrency(stmt.amount)}</span>` : '-',
        stmt.type === 'debit' ? `<span class="amount-negative">-${formatPrintCurrency(stmt.amount)}</span>` : '-',
        `<span class="${runningBalance >= 0 ? 'amount-positive' : 'amount-negative'}">${formatPrintCurrency(runningBalance)}</span>`
      ];
    });

    const summary = [
      { label: 'Opening Balance', value: formatPrintCurrency(monthlySummary.opening_balance), type: 'neutral' },
      { label: 'Total Deposits', value: formatPrintCurrency(monthlySummary.total_deposits), type: 'positive' },
      { label: 'Total Withdrawals', value: formatPrintCurrency(monthlySummary.total_withdrawals), type: 'negative' },
      { label: 'Closing Balance', value: formatPrintCurrency(monthlySummary.closing_balance), type: monthlySummary.closing_balance >= 0 ? 'positive' : 'negative' }
    ];

    const content = generatePrintContent({
      title: 'Account Statement (Passbook)',
      businessName: 'Shri Kripa Pharmacy',
      dateRange,
      headers,
      rows,
      summary
    });

    openPrintWindow(content);
    toast.success('Opening print preview...');
  }, [filteredStatements, selectedMonth, monthlySummary]);

  // Register Alt+P shortcut
  usePrintShortcut(handlePrint);

  // Combined modes for display lookup
  const allModes = [...paymentModes, ...depositModes];
  
  const getPaymentModeInfo = (mode) => {
    return allModes.find(pm => pm.value === mode) || { label: mode || 'Cash', icon: Banknote, color: 'text-slate-600 bg-slate-50 border-slate-200' };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-slate-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 max-w-6xl mx-auto" data-testid="ac-statement-container">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading tracking-tight text-slate-900">Passbook</h1>
            <p className="text-sm text-slate-500">Track deposits and withdrawals</p>
          </div>
          <div className="flex items-center gap-3">
            <MonthSelector value={selectedMonth} onChange={setSelectedMonth} />
            <Button onClick={handlePrint} variant="outline" className="h-9" title="Print (Alt+P)">
              <Printer className="h-4 w-4 mr-1.5" />Print
            </Button>
          <Dialog open={showAdd} onOpenChange={(open) => { setShowAdd(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-entry-button" className="bg-blue-600 hover:bg-blue-700 text-white h-9">
                  <Plus className="h-4 w-4 mr-1.5" />Add Entry
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-heading">{editingEntry ? 'Edit Entry' : 'Add Entry'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Type Toggle */}
                <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setNewEntry({ ...newEntry, type: 'credit', sync_to_khata: false, payment_mode: 'cash_deposit' })}
                    className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-all ${
                      newEntry.type === 'credit' 
                        ? 'bg-green-600 text-white shadow-sm' 
                        : 'text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <TrendingUp className="h-4 w-4 inline mr-1.5" />Deposit
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewEntry({ ...newEntry, type: 'debit', payment_mode: 'cash' })}
                    className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-all ${
                      newEntry.type === 'debit' 
                        ? 'bg-red-600 text-white shadow-sm' 
                        : 'text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <TrendingDown className="h-4 w-4 inline mr-1.5" />Withdrawal
                  </button>
                </div>

                {/* Payment Mode - Conditional based on type */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Payment Mode</Label>
                  {newEntry.type === 'credit' ? (
                    // Deposit: Show only Cash Deposit and Daily Paytm Settlement
                    <div className="grid grid-cols-2 gap-2">
                      {depositModes.map((mode) => {
                        const Icon = mode.icon;
                        const isSelected = newEntry.payment_mode === mode.value;
                        return (
                          <button
                            key={mode.value}
                            type="button"
                            onClick={() => setNewEntry({ ...newEntry, payment_mode: mode.value })}
                            className={`flex items-center justify-center gap-2 py-3 px-3 rounded-lg border text-sm font-medium transition-all ${
                              isSelected 
                                ? `${mode.color} border-current ring-2 ring-offset-1 ${mode.value === 'cash_deposit' ? 'ring-green-300' : 'ring-sky-300'}` 
                                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            {mode.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    // Withdrawal: Show all payment modes
                    <div className="grid grid-cols-3 gap-1.5">
                      {paymentModes.map((mode) => {
                        const Icon = mode.icon;
                        const isSelected = newEntry.payment_mode === mode.value;
                        return (
                          <button
                            key={mode.value}
                            type="button"
                            onClick={() => setNewEntry({ ...newEntry, payment_mode: mode.value })}
                            className={`flex items-center justify-center gap-1.5 py-2 px-2 rounded-md border text-xs font-medium transition-all ${
                              isSelected 
                                ? `${mode.color} border-current` 
                                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            <Icon className="h-3.5 w-3.5" />
                            {mode.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Party Selection - Only for Withdrawals */}
                {newEntry.type === 'debit' && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Party Name *</Label>
                    <PartyDropdown
                      parties={parties}
                      value={newEntry.party_id}
                      onChange={handlePartySelect}
                      placeholder="Select from CRM"
                    />
                  </div>
                )}

                {/* Amount and Date Row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Amount (₹) *</Label>
                    <Input
                      data-testid="entry-amount-input"
                      type="number"
                      step="0.01"
                      value={newEntry.amount}
                      onChange={(e) => setNewEntry({ ...newEntry, amount: e.target.value })}
                      placeholder="0.00"
                      required
                      className="h-10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Date *</Label>
                    <Input
                      data-testid="entry-date-input"
                      type="date"
                      value={newEntry.date}
                      onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })}
                      required
                      className="h-10"
                    />
                  </div>
                </div>

                {/* Cheque/Ref Number (show for cheque only) */}
                {newEntry.payment_mode === 'cheque' && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Cheque No.</Label>
                    <Input
                      value={newEntry.cheque_ref}
                      onChange={(e) => setNewEntry({ ...newEntry, cheque_ref: e.target.value })}
                      placeholder="Enter cheque number"
                      className="h-10"
                    />
                  </div>
                )}

                {/* Description - Optional */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Description <span className="text-slate-400 text-xs">(optional)</span></Label>
                  <Input
                    data-testid="entry-description-input"
                    value={newEntry.description}
                    onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
                    placeholder="e.g., Payment received for invoice"
                    className="h-10"
                  />
                </div>

                {/* Info banner for distributor auto-sync */}
                {newEntry.type === 'debit' && newEntry.party_type === 'distributor' && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <Check className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-blue-800">
                      This payment will automatically sync to Khatabook
                    </span>
                  </div>
                )}

                <Button type="submit" data-testid="submit-entry-button" className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white">
                  {editingEntry ? 'Save Changes' : 'Add Entry'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Confirmation Dialog for Edit */}
          <Dialog open={showConfirmEdit} onOpenChange={setShowConfirmEdit}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Confirm Modification
                </DialogTitle>
                <DialogDescription>
                  Are you sure you want to modify this entry?
                </DialogDescription>
              </DialogHeader>
              <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-600">
                <p><strong>Amount:</strong> ₹{pendingEditData?.amount?.toLocaleString('en-IN')}</p>
                <p><strong>Date:</strong> {pendingEditData?.date}</p>
                {pendingEditData?.party_name && <p><strong>Party:</strong> {pendingEditData.party_name}</p>}
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowConfirmEdit(false)}
                >
                  No, Cancel
                </Button>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={confirmEdit}
                >
                  Yes, Confirm
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Summary Cards - Opening, Deposits, Withdrawals, Closing */}
        <div className="grid grid-cols-4 gap-3">
          <Card className="p-4 border-slate-200 bg-slate-50/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Wallet className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-600 uppercase">Opening Bal</p>
                <p className="text-xl font-bold text-slate-700">₹{monthlySummary.opening_balance.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-green-200 bg-green-50/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-green-600 uppercase">Deposits</p>
                <p className="text-xl font-bold text-green-700">₹{monthlySummary.total_deposits.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-red-200 bg-red-50/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <TrendingDown className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-red-600 uppercase">Withdrawals</p>
                <p className="text-xl font-bold text-red-700">₹{monthlySummary.total_withdrawals.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-blue-200 bg-blue-50/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Wallet className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase">Closing Bal</p>
                <p className={`text-xl font-bold ${monthlySummary.closing_balance >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                  ₹{monthlySummary.closing_balance.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters Row */}
        <Card className="p-3 border-slate-200">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search party or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
            
            {/* Payment Mode Filter */}
            <div className="flex items-center gap-1.5">
              <Filter className="h-4 w-4 text-slate-400" />
              <select
                value={filterMode}
                onChange={(e) => setFilterMode(e.target.value)}
                className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
              >
                <option value="all">All Modes</option>
                <optgroup label="Deposits">
                  {depositModes.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Withdrawals">
                  {paymentModes.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </optgroup>
              </select>
            </div>
            
            {/* Clear Filters */}
            {(searchQuery || filterMode !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setFilterMode('all');
                }}
                className="h-9 text-slate-500"
              >
                <X className="h-4 w-4 mr-1" />Clear
              </Button>
            )}
          </div>
        </Card>

        {/* Transactions Table */}
        <Card className="border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-24">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Party Name</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-green-600 uppercase tracking-wider w-28">Deposit</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-red-600 uppercase tracking-wider w-28">Withdrawal</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider w-24">Mode</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-28">Cheque/Ref</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider w-16">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredStatements.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                      <FileText className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      <p>No transactions found</p>
                      {statements.length === 0 && (
                        <Button onClick={() => setShowAdd(true)} className="mt-3 bg-blue-600 hover:bg-blue-700 text-white">
                          Add First Entry
                        </Button>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredStatements.map((stmt) => {
                    const modeInfo = getPaymentModeInfo(stmt.payment_mode);
                    const ModeIcon = modeInfo.icon;
                    return (
                      <tr key={stmt.id} className="hover:bg-slate-50 transition-colors" data-testid={`statement-${stmt.id}`}>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-600">
                            {new Date(stmt.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{stmt.party_name || '-'}</p>
                            {stmt.description && <p className="text-xs text-slate-500 truncate max-w-[200px]">{stmt.description}</p>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {stmt.type === 'credit' && (
                            <span className="font-semibold text-green-600">
                              +₹{stmt.amount.toLocaleString('en-IN')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {stmt.type === 'debit' && (
                            <span className="font-semibold text-red-600">
                              -₹{stmt.amount.toLocaleString('en-IN')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${modeInfo.color}`}>
                            <ModeIcon className="h-3 w-3" />
                            {modeInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-600">{stmt.cheque_ref || '-'}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleEdit(stmt)}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Edit entry"
                            data-testid={`edit-entry-${stmt.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {/* Totals Footer */}
              {filteredStatements.length > 0 && (
                <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                  <tr>
                    <td className="px-4 py-3 font-semibold text-slate-700" colSpan={2}>
                      Total ({filteredStatements.length} entries)
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-green-700">
                      ₹{totals.deposits.toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-red-700">
                      ₹{totals.withdrawals.toLocaleString('en-IN')}
                    </td>
                    <td colSpan={3} className="px-4 py-3 text-right">
                      <span className="font-bold text-slate-700">
                        Balance: <span className={totals.balance >= 0 ? 'text-green-700' : 'text-red-700'}>
                          ₹{totals.balance.toLocaleString('en-IN')}
                        </span>
                      </span>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default ACStatement;
